import { useEffect, useState } from 'react';

interface SummaryPayload {
  text?: string | null;
  confidence?: number | null;
  state?: string | null;
  updated_at?: string | null;
}

interface Props {
  ticketId?: string | null;
  token?: string | null;
  className?: string;
  showState?: boolean;
}

export default function NumaSummaryChip({ ticketId, token, className = '', showState = true }: Props) {
  const [summary, setSummary] = useState<SummaryPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!ticketId || !token) {
      setSummary(null);
      setError('');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    const controller = new AbortController();

    const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
    fetch(`${CORE_API}/api/v1/tickets/${ticketId}/numa-summary`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload?.error || 'Unable to load summary');
        }
        return response.json();
      })
      .then((payload) => {
        if (cancelled) return;
        setSummary(payload?.summary || null);
      })
      .catch((err) => {
        if (cancelled || err?.name === 'AbortError') return;
        setSummary(null);
        setError(err?.message || 'NUMA unavailable');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [ticketId, token]);

  if (!ticketId || !token) return null;

  const labelParts: string[] = [];
  if (showState && summary?.state) {
    labelParts.push(summary.state.toUpperCase());
  } else {
    labelParts.push('NUMA');
  }
  if (typeof summary?.confidence === 'number' && Number.isFinite(summary.confidence)) {
    labelParts.push(`${Math.round(summary.confidence)}%`);
  }

  const label = labelParts.join(' • ');
  let body = 'Waiting for summary';
  if (loading) {
    body = 'Loading NUMA summary…';
  } else if (error) {
    body = 'NUMA summary unavailable';
  } else if (summary?.text) {
    body = summary.text;
  }

  return (
    <div
      className={`rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 shadow-sm ${className}`.trim()}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">{label}</p>
      <p
        className="mt-1 text-xs text-emerald-900"
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {body}
      </p>
      {summary?.updated_at ? (
        <p className="mt-1 text-[10px] uppercase tracking-wide text-emerald-600">
          Updated {new Date(summary.updated_at).toLocaleString()}
        </p>
      ) : null}
    </div>
  );
}
